import * as d3 from "d3";
import dateUtil from "../../../utils/dateUtil";

export default {
    getLoeEvents(self) {
        let loeEvents = {};
        Object.keys(self.events).forEach(eventKey => {
            const dataKey = eventKey;

            loeEvents[dataKey] = self.events[eventKey].loe_dates
                .map(e => {
                    let res = [];
                    const brandDetails = {
                        id: e.id,
                        brand_name: e.brand_name,
                        key: dataKey,
                        type: e.type
                    };
                    if (e.limited_loe !== null)
                        res.push({
                            ...brandDetails,
                            year: e.limited_loe.year,
                            month: e.limited_loe.month,
                            quarter: e.limited_loe.quarter,
                            date: e.limited_loe.date,
                            isLimited: true
                        });

                    if (e.full_loe !== null)
                        res.push({
                            ...brandDetails,
                            year: e.full_loe.year,
                            month: e.full_loe.month,
                            quarter: e.full_loe.quarter,
                            date: e.full_loe.date,
                            isLimited: false
                        });

                    return res;
                })
                .flat(1);
        });
        loeEvents = Object.fromEntries(
            Object.entries(loeEvents).filter(([key, value]) => value.length !== 0)
        );

        return loeEvents;
    },

    getQuarters(self) {
        return Object.values(self.loeEvents)
            .flat(1)
            .map(datapoint => `${datapoint.year} ${datapoint.quarter}`)
            .filter((datapoint, index, array) => array.indexOf(datapoint) === index)
            .sort((a, b) => {
                if (a > b) {
                    return 1;
                }

                if (a < b) {
                    return -1;
                }

                return 0;
            });
    },

    updateLoeEventsVisibility(self){
        self.svg
            .element
            .selectAll('.event-loe')
            .classed('active', self.showLoe);
    },
    
    removeAll(self){
        self.svg
            .element
            .selectAll('.event-loe')
            .remove();
    },

    drawLoeEvents(self, line, dataKey, isNew, animate, lineColor) {
        if (self.loeEventDateMap == null) self.loeEventDateMap = new Map();

        if (self.loeEvents[dataKey]) {
            if (isNew) {
                const eventsPerQuarter = {};
                self.loeEvents[dataKey].forEach(event => {
                    if (event) {
                        const quarter = `${event.year}_${event.quarter}`;

                        if (!eventsPerQuarter[quarter]) {
                            eventsPerQuarter[quarter] = [event];
                        } else {
                            eventsPerQuarter[quarter].push(event);
                        }
                    }
                });

                self.loeEvents[dataKey].forEach(async event => {
                    if (isNew && event) {
                        const loeTranslateXandY = this.getLoeTranslateXandY(self, event);
                        const eventColor = self.getLineColorAtDate(dataKey, event.date, lineColor);
                        if (loeTranslateXandY != null)
                            line.append(() => self.loeIcon.node().cloneNode(true))
                                .datum(event)
                                .attr("class", `event event-loe event-c-loe`)
                                .attr("style", `color: ${eventColor};`)
                                .attr("transform", loeTranslateXandY)
                                .on("mouseover", function mouseOver(d) {
                                    const position = d3
                                        .select(this)
                                        .node()
                                        .getBoundingClientRect();
                                    const quarter = `${d.year}_${d.quarter}`;
                                    const quarterEvents = eventsPerQuarter[quarter];
                                    self.tooltipMouseOver(position, d, quarterEvents, true);
                                })
                                .on("mouseleave", () => {
                                    self.tooltipMouseLeave();
                                });

                        line.append(() => self.xLoeIcon.node().cloneNode(true))
                            .datum(event)
                            .attr("class", `event event-loe event-x-loe`)
                            .attr("style", `color: ${eventColor};`)
                            .attr("transform", d => {
                                return this.getXLoeTranslateXandY(self, d);
                            })
                            .on("mouseover", function mouseOver(d) {
                                const position = d3
                                    .select(this)
                                    .node()
                                    .getBoundingClientRect();
                                const quarter = `${d.year}_${d.quarter}`;
                                const quarterEvents = eventsPerQuarter[quarter];
                                self.tooltipMouseOver(position, d, quarterEvents, true);
                            })
                            .on("mouseleave", () => {
                                self.tooltipMouseLeave();
                            });
                    }
                });
            } else {
                let circles = line.selectAll(".event-c-loe");

                if (animate) {
                    circles = circles.transition().duration(500);
                }

                circles.attr("transform", d => {
                    return this.getLoeTranslateXandY(self, d);
                });

                let xCircles = line.selectAll(".event-x-loe");

                if (animate) {
                    xCircles = xCircles.transition().duration(500);
                }

                xCircles.attr("transform", d => {
                    return this.getXLoeTranslateXandY(self, d);
                });
            }
        }
    },

    getLoeTranslateXandY(self, d) {
        const eventDate = new Date(d.date);

        let currentQuarter = Math.ceil((eventDate.getMonth() + 1) / 3);
        let yearToGet =
            currentQuarter === 4 ? eventDate.getFullYear() + 1 : eventDate.getFullYear();
        let quarterToGet = currentQuarter === 4 ? 1 : currentQuarter + 1;
        let nextRevItem = self.data[d.key].find(
            a => a.year === yearToGet && a.quarter === "Q" + quarterToGet.toString()
        );
        let currRevItem = self.data[d.key].find(a => a.year === d.year && a.quarter === d.quarter);
        if (currRevItem == null) return null;

        var quarter = Math.floor(eventDate.getMonth() / 3);
        var firstDate = new Date(eventDate.getFullYear(), quarter * 3, 1);
        var endDate = new Date(firstDate.getFullYear(), firstDate.getMonth() + 3, 0);

        const days = new Date(endDate - firstDate) / 1000 / 60 / 60 / 24;
        const eventDateDIff = new Date(eventDate - firstDate) / 1000 / 60 / 60 / 24;
        const datePercentage = (eventDateDIff * 100) / days;
        const totalQuaterDistance =
            self.xScale(self.getQuarterIndex(d) + 1) - self.xScale(self.getQuarterIndex(d));
        const ploatX = (datePercentage * totalQuaterDistance) / 100;

        const translateX = self.xScale(self.getQuarterIndex(d)) + ploatX;

        let revenue = nextRevItem
            ? parseFloat(nextRevItem.revenue)
            : parseFloat(currRevItem.revenue);
        let revDiff = revenue - parseFloat(currRevItem.revenue);
        let translateY = null;
        if (self.loeEventDateMap.has(d.date + d.key) && currRevItem.revenue) {
            let add = parseInt(self.loeEventDateMap.get(d.date + d.key)) - 14;
            self.loeEventDateMap.delete(d.date + d.key);
            self.loeEventDateMap.set(d.date + d.key, add + "");
            translateY = add;
        } else {
            translateY = self.yScale(
                parseFloat(currRevItem.revenue) * 1 + (datePercentage / 100) * revDiff
            );
            self.loeEventDateMap.set(d.date + d.key, translateY);
        }

        return `translate(${translateX - self.loeIconBBox.width / 2}, ${translateY -
            self.loeIconBBox.height / 2})`;
    },
    getXLoeTranslateXandY(self, d) {
        const eventDate = new Date(d.date);
        var quarter = Math.floor(eventDate.getMonth() / 3);
        var firstDate = new Date(eventDate.getFullYear(), quarter * 3, 1);
        var endDate = new Date(firstDate.getFullYear(), firstDate.getMonth() + 3, 0);

        const days = new Date(endDate - firstDate) / 1000 / 60 / 60 / 24;
        const eventDateDIff = new Date(eventDate - firstDate) / 1000 / 60 / 60 / 24;
        const datePercentage = (eventDateDIff * 100) / days;
        const totalQuaterDistance =
            self.xScale(self.getQuarterIndex(d) + 1) - self.xScale(self.getQuarterIndex(d));
        const ploatX = (datePercentage * totalQuaterDistance) / 100;

        const translateX = self.xScale(self.getQuarterIndex(d)) + ploatX;

        let translateY = null;
        const mapKey =
            "x-loe-" + d.year + (d.month != null ? d.month.toString().padStart(2, "0") : "01");

        const innerMapKey = d.id + (d.isLimited ? "-limited" : "-full");
        if (self.loeEventDateMap.has(mapKey)) {
            const map = self.loeEventDateMap.get(mapKey);
            if (map.has(innerMapKey)) translateY = map.get(innerMapKey);
            else {
                const getLastValueInMap = map => Array.from(map.values()).pop();
                const lastItemInMap = getLastValueInMap(map);
                let add = lastItemInMap - 14;
                self.loeEventDateMap.delete(mapKey);

                map.set(innerMapKey, add);

                self.loeEventDateMap.set(mapKey, map);
                translateY = add;
            }
        } else {
            translateY = self.yScale(0) - self.xLoeIconBBox.height;
            const map = new Map();
            map.set(innerMapKey, translateY);

            self.loeEventDateMap.set(mapKey, map);
        }

        return `translate(${translateX - self.xLoeIconBBox.width / 2}, ${translateY})`;
    },

    updateTooltipInfo(text, data) {
        let eventTitle = data.isLimited
            ? "Limited Loss of Exclusivity"
            : "Full Loss of Exclusivity";

        let loe_date =
            (data.month ? dateUtil.getMonthShortName(data.month) : "?").toString() +
            " " +
            data.year;
        text.append("p")
            .attr("class", "event-type")
            .html(eventTitle);

        text.append("p")
            .attr("class", "event-date")
            .html(loe_date);
    }
};